// src/lib/resources/relevance/topic-hub-affinity.ts

/**
 * Topic-hub affinity scoring for related-resource ranking.
 *
 * Resources that share a curated topic hub are more likely to be
 * genuinely related for discovery — e.g. hook-rate-benchmark and
 * thruplay-rate-benchmark both live in creative-analytics and
 * video-performance. Boosting shared-hub pairs improves the topical
 * graph that crawlers and on-site discover surfaces traverse.
 */

import {
  GLOSSARY_TOPIC_IDS,
  TOOL_TOPIC_IDS,
} from "~/lib/resources/relevance/topic-hub-affinity-map"
import type { ResourceTopicId } from "~/lib/resources/topics/types"

/** Points added per shared topic hub between two resources. */
const SHARED_TOPIC_HUB_BOOST = 6
const MAX_SHARED_TOPIC_HUB_BOOST = 18

/**
 * Count shared topic hubs between two topic-id lists.
 *
 * @param {readonly ResourceTopicId[]} left - First hub membership list.
 * @param {readonly ResourceTopicId[]} right - Second hub membership list.
 * @returns {number} Number of shared hub ids.
 */
function countSharedTopicIds(
  left: readonly ResourceTopicId[],
  right: readonly ResourceTopicId[],
): number {
  if (left.length === 0 || right.length === 0) return 0
  const rightSet = new Set(right)
  let shared = 0
  for (const id of left) {
    if (rightSet.has(id)) shared++
  }
  return shared
}

/**
 * Resolve topic hub ids for a tool slug.
 *
 * @param {string} toolSlug - Canonical tool slug.
 * @returns {readonly ResourceTopicId[]} Topic ids listing this tool.
 */
export function getTopicIdsForToolSlug(
  toolSlug: string,
): readonly ResourceTopicId[] {
  return TOOL_TOPIC_IDS[toolSlug] ?? []
}

/**
 * Resolve topic hub ids for a glossary term.
 *
 * @param {string} glossaryCategory - Glossary category segment.
 * @param {string} glossarySlug - Glossary term slug.
 * @returns {readonly ResourceTopicId[]} Topic ids listing this term.
 */
export function getTopicIdsForGlossaryTerm(
  glossaryCategory: string,
  glossarySlug: string,
): readonly ResourceTopicId[] {
  return GLOSSARY_TOPIC_IDS[`${glossaryCategory}:${glossarySlug}`] ?? []
}

/**
 * Score how strongly two tools share curated topic hubs.
 *
 * @param {string} currentSlug - Slug of the page being viewed.
 * @param {string} candidateSlug - Slug of the candidate related tool.
 * @returns {number} Affinity boost (0 when no shared hubs).
 */
export function scoreSharedTopicHubAffinity(
  currentSlug: string,
  candidateSlug: string,
): number {
  if (currentSlug === candidateSlug) return 0

  const shared = countSharedTopicIds(
    getTopicIdsForToolSlug(currentSlug),
    getTopicIdsForToolSlug(candidateSlug),
  )
  return Math.min(shared * SHARED_TOPIC_HUB_BOOST, MAX_SHARED_TOPIC_HUB_BOOST)
}

/**
 * Score how strongly a glossary term shares topic hubs with a tool.
 *
 * @param {string} glossaryCategory - Glossary category segment.
 * @param {string} glossarySlug - Glossary term slug.
 * @param {string} toolSlug - Tool slug for the page being viewed.
 * @returns {number} Affinity boost (0 when no shared hubs).
 */
export function scoreGlossaryTopicHubAffinity(
  glossaryCategory: string,
  glossarySlug: string,
  toolSlug: string,
): number {
  const shared = countSharedTopicIds(
    getTopicIdsForGlossaryTerm(glossaryCategory, glossarySlug),
    getTopicIdsForToolSlug(toolSlug),
  )
  return Math.min(shared * SHARED_TOPIC_HUB_BOOST, MAX_SHARED_TOPIC_HUB_BOOST)
}
